import { Col, Label, Row } from "reactstrap";
// select Input
import Select from "react-select";
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import "./VariablesSelectByTypes.css";
import VariableDetails from "./VariableDetails";
import type { VariableDetailsRecord } from "./VariableDetails";

/** One variable as `specific_variables_list` returns it (the fields this picker reads). */
type VariableRecord = VariableDetailsRecord & {
  _id: string;
  code_name?: string;
  char_number?: number;
};

/** A react-select option wrapping one variable. */
type VariableOption = { value: VariableRecord; label: string; _id: string };

/** The entry handed to `handleVariableChange` per selected variable. */
export type SelectedTemplateVariable = {
  variable?: string;
  type?: string;
  value: { name: string; link: string } | never[] | string;
  complextype?: string;
  char_number?: number;
  _id: string;
};

type VariablesSelectByTypesProps<T extends { _id: string }> = {
  status: string;
  type: string;
  inputLabel?: string;
  handleVariableChange: (vars: SelectedTemplateVariable[], status: string) => void;
  dataValue: T[];
  show?: boolean;
  /**
   * When set, each selected variable gets ↑/↓ buttons that hand back
   * `dataValue` with that entry swapped with its neighbour — the stored
   * entries themselves, so their values survive (SCRUM-2188).
   */
  onReorder?: (vars: T[], status: string) => void;
};

const VariablesSelectByTypes = <T extends { _id: string }>(props: VariablesSelectByTypesProps<T>) => {
  const { status, type, inputLabel, handleVariableChange, dataValue, show, onReorder } = props;
  const [variables, setVariables] = useState<VariableOption[]>([]);
  // The selection follows the STORED order, not the API list's (SCRUM-2188):
  // react-select hands onChange the chips in this order plus the new pick, so
  // an API-ordered selection would rewrite the stored order on every edit.
  const selectedVars = dataValue
    .map((d) => variables.find((v) => v._id === d._id))
    .filter((v): v is VariableOption => v !== undefined);
  const [modal, setModal] = useState(false);
  const [details, setDetails] = useState<Partial<VariableDetailsRecord>>({});
  const SelectVariablesHandler = (selected: readonly VariableOption[] | null) => {
    const value = selected === null ? [] : selected;
     
    const cleanedValues: SelectedTemplateVariable[] = value.map((elm) => {
      if (elm.value.variable_type === 'Link'){
        return {
          variable: elm.value.code_name,
          type: elm.value.variable_type,
          value: {name:"", link:""},
          complextype: elm.value.variable_complex_type,
          char_number: elm.value.char_number,
          _id: elm.value._id,
        };
      }
      else if (elm.value.variable_type === 'Array'){
        return {
          variable: elm.value.code_name,
          type: elm.value.variable_type,
          value: [],
          complextype: elm.value.variable_complex_type,
          char_number: elm.value.char_number,

          _id: elm.value._id,
        };
      }
      else{
        return {
          variable: elm.value.code_name,
          type: elm.value.variable_type,
          value: "",
          complextype: elm.value.variable_complex_type,
          char_number: elm.value.char_number,

          _id: elm.value._id,
        };
      }
      
    });

    handleVariableChange(cleanedValues, status);
  };
  const moveVariable = (index: number, delta: -1 | 1) => {
    if (!onReorder || index + delta < 0 || index + delta >= dataValue.length) return;
    const next = [...dataValue];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    onReorder(next, status);
  };
  const handleShowDetails = (elem: VariableRecord) => {
    setDetails(elem);
    toggle();
  };
  const toggle = () => setModal(!modal);
  useEffect(() => {
    const getVariablesApi = async () => {
      return await api
        .get(
          `/api/variables/specific_variables_list?type=${type}&status=${status}`
        )
        .then(
          (data): VariableOption[] =>
            data.data.map((elem: VariableRecord) => {
              return { value: elem, label: elem.title, _id: elem._id };
            }) || []
        );
    };
    getVariablesApi().then((rslt) => {
      setVariables(rslt);
    });
  }, [status]);


  return (
    <>
      <Row>
      {show &&
           <Label
          className="form-control-label"
          htmlFor="example-text-input"
          md="2"
        >
          {inputLabel}
        </Label>
}
        
        {/*removed because a user shouldn't be able to edit page variables when creating a page*/}

        {/*  <Col md="10">
            <Select
            classNamePrefix="select"
            value={selectedVars}
            name={"variable " + status}
            options={variables}
            onChange={SelectVariablesHandler}
            isClearable="true"
            isSearchable="true"
            isMulti
            required
          />
        </Col>
      */}
{show &&
      <Col md="10">
            <Select
            classNamePrefix="select"
            value={selectedVars}
            name={"variable " + status}
            options={variables}
            onChange={SelectVariablesHandler}
            isClearable={true}
            isSearchable={true}
            isMulti
            required={status === "required"}
          />
        </Col>
}
        
        <Col md="10">
          <Row>
            {selectedVars.map((elem) => {
              const name = elem.value.code_name ?? elem.label;
              // Position in the STORED array, which is what a move swaps.
              const index = dataValue.findIndex((d) => d._id === elem.value._id);
              return (
                <div className="varible_container" key={elem.value._id}>
                  <div
                      className="button_variables"
                      key={elem.value._id}
                      onClick={() => handleShowDetails(elem.value)}
                  >
                    {elem.label}
                  </div>
                  {onReorder && (
                    <span className="variable_order_buttons">
                      <button
                        type="button"
                        aria-label={`Move ${name} up`}
                        disabled={index === 0}
                        onClick={() => moveVariable(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${name} down`}
                        disabled={index === dataValue.length - 1}
                        onClick={() => moveVariable(index, 1)}
                      >
                        ↓
                      </button>
                    </span>
                  )}
                </div>
              );
            })}
          </Row>
        </Col>

      </Row>

      {Object.keys(details).length === 0 ? null : (
        <VariableDetails details={details as VariableDetailsRecord} modal={modal} toggle={toggle} />
      )}
    </>
  );
};

export default VariablesSelectByTypes;
